import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { AppDialog } from "@/components/app/AppDialog";
import { AppSelect } from "@/components/app/AppSelect";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { formatNumber } from "@/lib/format";
import {
  bulkRequest,
  BulkRunPhase,
  BulkTokenAction,
  ExpirationMode,
  TAGS_MODES,
  TagsMode,
  useBulkTokenRun,
  type BulkRun,
  type BulkTokenParams,
  type TokenSelection,
} from "@/lib/bulkTokens";
import type { TokenFilter } from "@/lib/tokens";
import { ErrBox, tokenInputCls } from "@/pages/tokens/TokenActionDialogs";

/**
 * Dialogy hromadných akcí nad vybranými tokeny.
 *
 * Vlastní soubor, ne zobecnění {@link TokenActionDialogs}: životní cyklus je jiný
 * (formulář → běh → souhrn, po dokončení se dialog nezavírá sám a za běhu nesmí zmizet
 * kliknutím vedle) a parametry taky (režim tagů, režim expirace, obejití auth serveru).
 * Zobecnění by protlačilo sjednocení „jeden token | výběr" do pěti komponent, které
 * používá i detail tokenu.
 */

export interface BulkScope {
  /** Odhad počtu z `totalElements`; autoritativní číslo přijde ze serveru v průběhu. */
  count: number;
  selection: TokenSelection;
  /** Debounced filtr — musí to být týž objekt, který dostal seznam. */
  filter: TokenFilter;
  /** Kolik z *viditelných* vybraných řádků má akci zakázanou (jen informativně). */
  blockedVisible: number;
}

interface BulkDialogProps {
  scope: BulkScope;
  onClose: () => void;
  /** Běh doběhl → volající zruší výběr a obnoví seznam. */
  onFinished: () => void;
}

/** Souhrn a průběh běhu. */
function BulkRunView({ run }: { run: BulkRun }) {
  const { t } = useTranslation();
  const progress = run.progress;
  const total = progress?.total ?? 0;
  const processed = progress?.processed ?? 0;
  const percent = total > 0 ? Math.round((processed / total) * 100) : 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
        <div
          className="h-full rounded-full bg-brand-solid transition-[width]"
          style={{ width: `${percent}%` }}
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>

      <p className="text-sm text-secondary tabular-nums">
        {t("tokens.bulk.progress", {
          processed: formatNumber(processed),
          total: formatNumber(total),
        })}
      </p>

      {run.result && (
        <div className="flex flex-wrap gap-2">
          <Badge type="pill-color" size="sm" color="success">
            {t("tokens.bulk.summary.succeeded")}: {formatNumber(run.result.succeeded)}
          </Badge>
          <Badge type="pill-color" size="sm" color="error">
            {t("tokens.bulk.summary.failed")}: {formatNumber(run.result.failed)}
          </Badge>
          <Badge type="pill-color" size="sm" color="gray">
            {t("tokens.bulk.summary.skipped")}: {formatNumber(run.result.skipped)}
          </Badge>
        </div>
      )}

      {run.result && run.result.failures.length > 0 && (
        <details className="rounded-lg bg-secondary p-3 ring-1 ring-secondary">
          <summary className="cursor-pointer text-sm font-medium text-error-primary">
            {t("tokens.bulk.summary.errors", { count: run.result.failed })}
          </summary>
          <ul className="mt-2 list-inside list-disc text-sm text-secondary">
            {run.result.failures.map((f) => (
              <li key={f.tokenId}>
                <span className="font-mono">{f.tokenId}</span> — {f.message}
              </li>
            ))}
          </ul>
          {run.result.detailsTruncated && (
            <p className="mt-1 text-xs text-tertiary">{t("tokens.bulk.summary.detailsTruncated")}</p>
          )}
        </details>
      )}

      {run.result && run.result.skipped > 0 && (
        <p className="text-xs text-tertiary">{t("tokens.bulk.summary.skippedHint")}</p>
      )}

      {run.aborted && (
        <p className="text-sm text-warning-primary">
          {t("tokens.bulk.stopped", {
            processed: formatNumber(processed),
            total: formatNumber(total),
          })}
        </p>
      )}

      <ErrBox msg={run.error} />
    </div>
  );
}

/** Společná kostra: formulář → běh → souhrn. */
function BulkDialogShell({
  scope,
  onClose,
  onFinished,
  title,
  action,
  destructive = false,
  buildParams,
  children,
}: BulkDialogProps & {
  title: string;
  action: BulkTokenAction;
  destructive?: boolean;
  /** Validace + parametry akce; vrácený `string` je chybová hláška. */
  buildParams: () => BulkTokenParams | string;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const run = useBulkTokenRun();
  const [confirmed, setConfirmed] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Nevratné akce chce potvrdit i server (400 bez `confirmed`), stejně jako rozsah
  // „všechny tokeny" — prázdný filtr bez jediného kritéria.
  const needsConfirmation =
    destructive || (scope.selection.all && Object.keys(scope.filter).every((k) => !scope.filter[k as keyof TokenFilter]));

  const submit = () => {
    const params = buildParams();
    if (typeof params === "string") {
      setErr(params);
      return;
    }
    setErr(null);
    void run.start(
      bulkRequest(action, scope.selection, scope.filter, params, {
        confirmed: !needsConfirmation || confirmed,
        expectedCount: scope.count,
      }),
    );
  };

  const close = () => {
    run.cancel();
    if (run.phase === BulkRunPhase.DONE) onFinished();
    onClose();
  };

  return (
    <AppDialog
      isOpen
      onClose={close}
      // Za běhu nesmí dialog zmizet kliknutím vedle — akce by běžela dál naslepo.
      isDismissable={run.phase !== BulkRunPhase.RUNNING}
      size="lg"
      title={title}
      description={
        scope.selection.all
          ? t("tokens.bulk.scope.filter", { tokens: formatNumber(scope.count) })
          : t("tokens.bulk.scope.ids", { tokens: formatNumber(scope.count) })
      }
      footer={
        run.phase === BulkRunPhase.FORM ? (
          <>
            <Button color="secondary" onPress={close}>
              {t("common.cancel")}
            </Button>
            <Button
              color={destructive ? "primary-destructive" : "primary"}
              isDisabled={(needsConfirmation && !confirmed) || scope.count === 0}
              onPress={submit}
            >
              {t("tokens.bulk.start")}
            </Button>
          </>
        ) : run.phase === BulkRunPhase.RUNNING ? (
          <Button color="secondary-destructive" onPress={run.cancel}>
            {t("tokens.bulk.stop")}
          </Button>
        ) : (
          <Button color="primary" onPress={close}>
            {t("common.close")}
          </Button>
        )
      }
    >
      {run.phase === BulkRunPhase.FORM ? (
        <div className="flex flex-col gap-3">
          {children}
          {scope.blockedVisible > 0 && (
            <div className="rounded-lg bg-primary px-3 py-2 text-xs text-warning-primary ring-1 ring-warning">
              {t("tokens.bulk.blockedHint", { count: scope.blockedVisible })}
            </div>
          )}
          {needsConfirmation && (
            <Checkbox
              isSelected={confirmed}
              onChange={setConfirmed}
              size="md"
              label={t("tokens.bulk.confirmUnderstand", { tokens: formatNumber(scope.count) })}
            />
          )}
          <ErrBox msg={err} />
        </div>
      ) : (
        <BulkRunView run={run} />
      )}
    </AppDialog>
  );
}

export function BulkDeleteDialog(props: BulkDialogProps) {
  const { t } = useTranslation();
  return (
    <BulkDialogShell
      {...props}
      action={BulkTokenAction.DELETE}
      destructive
      title={t("tokens.action.delete")}
      buildParams={() => ({})}
    >
      <p className="text-sm text-secondary">{t("tokens.bulk.deleteWarning")}</p>
    </BulkDialogShell>
  );
}

export function BulkDeactivateDialog(props: BulkDialogProps) {
  const { t } = useTranslation();
  const [bypass, setBypass] = useState(false);
  return (
    <BulkDialogShell
      {...props}
      action={BulkTokenAction.DEACTIVATE}
      destructive
      title={t("tokens.action.deactivate")}
      buildParams={() => ({ bypassAuthServer: bypass })}
    >
      <p className="text-sm text-secondary">{t("tokens.bulk.deactivateWarning")}</p>
      <Checkbox
        isSelected={bypass}
        onChange={setBypass}
        size="md"
        label={t("tokens.bulk.bypassAuthServer")}
        hint={t("tokens.bulk.bypassAuthServerHint")}
      />
    </BulkDialogShell>
  );
}

export function BulkTagsDialog(props: BulkDialogProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<TagsMode>(TagsMode.ADD);
  const [value, setValue] = useState("");

  return (
    <BulkDialogShell
      {...props}
      action={BulkTokenAction.TAGS}
      title={t("tokens.action.tags")}
      buildParams={() => {
        const tags = value
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        if (tags.length === 0) return t("tokens.bulk.emptyTags");
        return { mode, tags };
      }}
    >
      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium text-tertiary">{t("tokens.bulk.tagsMode")}</span>
        <AppSelect value={mode} onChange={(e) => setMode(e.target.value as TagsMode)} fullWidth>
          {TAGS_MODES.map((m) => (
            <option key={m} value={m}>
              {t(`tokens.bulk.tagsMode.${m}`)}
            </option>
          ))}
        </AppSelect>
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium text-tertiary">{t("tokens.tagsLabel")}</span>
        <input
          className={tokenInputCls}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="smoke, zatez-2026"
        />
      </label>
    </BulkDialogShell>
  );
}

export function BulkExpirationDialog(props: BulkDialogProps) {
  const { t } = useTranslation();
  const [expiresAt, setExpiresAt] = useState("");
  const [ttl, setTtl] = useState("");
  const [clear, setClear] = useState(false);

  return (
    <BulkDialogShell
      {...props}
      action={BulkTokenAction.EXPIRATION}
      title={t("tokens.action.expiration")}
      buildParams={() => {
        if (clear) return { mode: ExpirationMode.CLEAR };
        const ttlValue = ttl.trim();
        if (ttlValue) return { mode: ExpirationMode.SET, expiresInSeconds: Number.parseInt(ttlValue, 10) };
        if (!expiresAt) return t("tokens.bulk.expirationRequired");
        return { mode: ExpirationMode.SET, expiresAt: `${expiresAt}:00` };
      }}
    >
      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium text-tertiary">{t("tokens.field.expires")}</span>
        <input
          type="datetime-local"
          className={tokenInputCls}
          value={expiresAt}
          disabled={clear || ttl.trim() !== ""}
          onChange={(e) => setExpiresAt(e.target.value)}
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium text-tertiary">{t("tokens.expiration.ttlSeconds")}</span>
        <input
          type="number"
          min={1}
          className={tokenInputCls}
          value={ttl}
          disabled={clear}
          onChange={(e) => setTtl(e.target.value)}
        />
        <span className="text-xs text-tertiary">{t("tokens.bulk.ttlHint")}</span>
      </label>
      {/* Zrušení expirace je u hromadné akce explicitní volba, ne „nechám pole prázdná“ —
          u tisíců tokenů je zapomenuté pole incident, ne nehoda. */}
      <Checkbox
        isSelected={clear}
        onChange={setClear}
        size="md"
        label={t("tokens.expiration.clear")}
      />
    </BulkDialogShell>
  );
}
